import * as THREE from 'three';
import {
  DRILL_TYPE,
  EDGE_QUALITY,
  RAD_TO_DEG,
  DURATION_MODE,
  TIMER_STATE,
  type DominantHand,
  type DurationMode,
  type EdgeQuality,
  type Zone,
} from '../config/constants';
import { highlightZoneBand } from '../pell/mesh';
import { StrikeDetector, type Strike } from '../hit-detection/strikeDetector';
import { type StrokeClassification } from '../hit-detection/strokeClassify';
import { evaluateRep, type DrillTarget, type MismatchReason } from './repEvaluator';
import { DrillRuntimeBase, type RuntimeSettings } from './drillRuntime';
import { formatClock } from './timer';
import { aggregate, type DrillStats, type Shot } from './stats';
import type { SessionStats } from './stats';
import { t } from '../i18n';

export type RuntimeOptions = {
  scene: THREE.Scene;
  /** Viewer camera — the hit decal billboards toward it (cut/edge bars face the user). */
  camera: THREE.Camera;
  pell: THREE.Mesh;
  /** Sword visual group; tracker samples its world frame (so live rotation offsets are honored). */
  sword: THREE.Object3D;
  swordController: THREE.Object3D;
  offHandController: THREE.Object3D;
  durationMs: number;
  userHeightCm: number;
  durationMode: DurationMode;
  /** Sword hand — flips the mandritto/riverso convention in stroke classification. */
  dominantHand: DominantHand;
  /** Minimum impact tip speed (m/s) for a valid colpo. */
  minStrikeSpeedMPerSec: number;
  /** Minimum swing-arc length (m) for a valid colpo. */
  minStrikeArcM: number;
  /** Present ⇒ prescriptive drill: each cut is scored against this target. */
  prescriptiveTarget?: DrillTarget;
  /** Correct-rep goal (required when durationMode === 'reps'). */
  repsTarget?: number;
  onEnd: (stats: SessionStats) => void;
};

export class SessionRuntime extends DrillRuntimeBase {
  override readonly drillType = DRILL_TYPE.pell;

  private detector: StrikeDetector;
  private shots: Shot[] = [];

  // Prescriptive drill state.
  private correctReps = 0;
  private repAttempts = 0;
  private reasonCounts: Partial<Record<MismatchReason | 'ambiguo', number>> = {};
  private lastNote: { text: string; ok: boolean } | null = null;
  private startedAtMs = 0;
  private edgeSumCorrectDeg = 0;

  constructor(private opts: RuntimeOptions) {
    super(opts);
    this.detector = new StrikeDetector({
      pell: opts.pell,
      camera: opts.camera,
      dominantHand: opts.dominantHand,
      userHeightCm: opts.userHeightCm,
      minSpeedMPerSec: opts.minStrikeSpeedMPerSec,
      minArcM: opts.minStrikeArcM,
    });
  }

  start(): void {
    if (this.opts.durationMode === DURATION_MODE.timed) {
      this.timer.start(this.opts.durationMs, performance.now());
    }
    this.detector.reset();
    this.shots = [];
    this.correctReps = 0;
    this.repAttempts = 0;
    this.reasonCounts = {};
    this.lastNote = null;
    this.startedAtMs = performance.now();
    this.edgeSumCorrectDeg = 0;
  }

  /** Live swap of the pell mesh (e.g. when user height changes). Caller adds/removes from scene. */
  setPell(pell: THREE.Mesh): void {
    this.opts.pell = pell;
    this.detector.setPell(pell);
    // A fresh pell starts with no zone fill — re-apply the prescriptive highlight.
    highlightZoneBand(pell, this.opts.prescriptiveTarget?.zone ?? null);
  }

  setUserHeightCm(cm: number): void {
    this.opts.userHeightCm = cm;
    this.detector.setUserHeightCm(cm);
  }

  /** Update the sword hand (stroke classification flips mandritto/riverso by it). */
  private setDominantHand(hand: DominantHand): void {
    this.opts.dominantHand = hand;
    this.detector.setDominantHand(hand);
  }

  /** Apply pell-mode settings; keys for other drills are ignored. */
  applySettings(settings: Partial<RuntimeSettings>): void {
    if (settings.dominantHand !== undefined) this.setDominantHand(settings.dominantHand);
    if (settings.pellMinSpeed !== undefined) {
      this.opts.minStrikeSpeedMPerSec = settings.pellMinSpeed;
      this.detector.setMinSpeedMPerSec(settings.pellMinSpeed);
    }
    if (settings.pellMinArc !== undefined) {
      this.opts.minStrikeArcM = settings.pellMinArc;
      this.detector.setMinArcM(settings.pellMinArc);
    }
  }

  protected onControllersChanged(): void {
    this.detector.reset();
  }

  end(): SessionStats {
    const drillTarget = this.opts.prescriptiveTarget;
    if (this.opts.durationMode === DURATION_MODE.reps && drillTarget) {
      const elapsedMs = Math.max(0, performance.now() - this.startedAtMs);
      const stats: DrillStats = {
        kind: 'drill',
        target: drillTarget,
        correct: this.correctReps,
        attempts: this.repAttempts,
        accuracyPct: this.repAttempts > 0 ? (this.correctReps / this.repAttempts) * 100 : 0,
        elapsedMs,
        avgEdgeDeg: this.correctReps > 0 ? this.edgeSumCorrectDeg / this.correctReps : 0,
        reasonCounts: {
          zona: this.reasonCounts.zona ?? 0,
          lato: this.reasonCounts.lato ?? 0,
          inclinazione: this.reasonCounts.inclinazione ?? 0,
          filo: this.reasonCounts.filo ?? 0,
          piattonata: this.reasonCounts.piattonata ?? 0,
          ambiguo: this.reasonCounts.ambiguo ?? 0,
        },
      };
      this.persistSession({
        elapsedMs,
        durationMs: elapsedMs,
        userHeightCm: this.opts.userHeightCm,
        interrupted: this.correctReps < (this.opts.repsTarget ?? 0),
        stats,
      });
      return stats;
    }

    const stats = aggregate(this.shots);
    if (this.opts.durationMode === DURATION_MODE.timed) {
      const now = performance.now();
      this.timer.end(now);
      const elapsedMs = this.timer.elapsedMs(now);
      this.persistSession({
        elapsedMs,
        durationMs: this.opts.durationMs,
        userHeightCm: this.opts.userHeightCm,
        interrupted: elapsedMs < this.opts.durationMs,
        stats,
      });
    }
    return stats;
  }

  /** Call from renderer.setAnimationLoop. */
  tick(): void {
    const dt = this.clock.getDelta();
    const now = performance.now();

    if (
      this.opts.durationMode === DURATION_MODE.timed &&
      this.timer.getState() !== TIMER_STATE.running
    ) {
      this.renderHud(now);
      return;
    }

    const strike = this.detector.detect(this.opts.sword, dt);
    if (strike) {
      // sets lastNote + counters (no-op if not a drill)
      this.evalPrescriptiveRep(
        strike.stroke,
        strike.zone,
        strike.quality,
        strike.edgeAngleRad * RAD_TO_DEG,
      );
      this.registerHit(now, strike);
    }

    if (this.opts.durationMode === DURATION_MODE.timed && this.timer.isExpired(now)) {
      const stats = this.end();
      this.opts.onEnd(stats);
      return;
    }

    if (
      this.opts.durationMode === DURATION_MODE.reps &&
      this.opts.repsTarget !== undefined &&
      this.correctReps >= this.opts.repsTarget
    ) {
      const stats = this.end();
      this.opts.onEnd(stats);
      return;
    }

    this.renderHud(now);
  }

  /** Score one cut against the drill target (no-op outside a prescriptive drill). */
  private evalPrescriptiveRep(
    stroke: StrokeClassification | null,
    zone: Zone,
    quality: EdgeQuality,
    edgeDeg: number,
  ): void {
    const target = this.opts.prescriptiveTarget;
    if (!target) return;
    this.repAttempts++;
    const verdict = evaluateRep(
      stroke ?? { lato: null, inclinazione: null, filo: null },
      zone,
      quality,
      target,
    );
    if (verdict.kind === 'match') {
      this.correctReps++;
      this.edgeSumCorrectDeg += edgeDeg;
      this.lastNote = { text: t('drillNoteMatch'), ok: true };
    } else if (verdict.kind === 'mismatch') {
      this.reasonCounts[verdict.reason] = (this.reasonCounts[verdict.reason] ?? 0) + 1;
      this.lastNote = { text: drillReasonLabel(verdict.reason), ok: false };
    } else {
      this.reasonCounts.ambiguo = (this.reasonCounts.ambiguo ?? 0) + 1;
      this.lastNote = { text: t('drillNoteAmbiguo'), ok: false };
    }
  }

  private registerHit(nowMs: number, strike: Strike): void {
    const { point, quality, zone, speed, edgeDir, cutDir } = strike;
    const edgeDeg = strike.edgeAngleRad * RAD_TO_DEG;
    this.shots.push({
      tMs: nowMs,
      zone,
      tipSpeedMPerSec: speed,
      edgeAngleDeg: edgeDeg,
      cutAngleDeg: strike.cutAngleDeg,
      stroke: strike.stroke,
      quality,
    });
    this.feedback.fire(
      quality === EDGE_QUALITY.thrust
        ? { kind: 'thrust', point, speed }
        : { kind: 'cut', point, quality, edgeDir, cutDir, speed, cutLengthM: strike.swingArcM },
    );
    this.hud.render({
      timerLabel: this.hudLabel(nowMs),
      lastShot: { zone, speed, edgeDeg },
      ...(this.lastNote ? { note: this.lastNote } : {}),
    });
  }

  private renderHud(nowMs: number): void {
    this.hud.render({
      timerLabel: this.hudLabel(nowMs),
      lastShot: this.lastShotView(),
      ...(this.lastNote ? { note: this.lastNote } : {}),
    });
  }

  private hudLabel(nowMs: number): string {
    if (this.opts.durationMode === DURATION_MODE.reps) {
      return `${this.correctReps}/${this.opts.repsTarget ?? 0}`;
    }
    if (this.opts.durationMode === DURATION_MODE.free) return t('freeHits')(this.shots.length);
    return formatClock(this.timer.remainingMs(nowMs));
  }

  private lastShotView(): { zone: string; speed: number; edgeDeg: number } | null {
    const s = this.shots[this.shots.length - 1];
    if (!s) return null;
    return { zone: s.zone, speed: s.tipSpeedMPerSec, edgeDeg: s.edgeAngleDeg };
  }
}

/** HUD verdict label for a mismatch reason (the dominant fault, per the cascade). */
function drillReasonLabel(reason: MismatchReason): string {
  switch (reason) {
    case 'zona':
      return t('drillReasonZona');
    case 'lato':
      return t('drillReasonLato');
    case 'inclinazione':
      return t('drillReasonInclinazione');
    case 'filo':
      return t('drillReasonFilo');
    case 'piattonata':
      return t('drillReasonPiattonata');
  }
}
